import { getOperatorOptions } from '../logicHelpers'
import { normalizePathInput, validatePath } from '../../services/validationHelpers'

const pageviewsSource = () => {
  return {
    types: [
      { label: 'Pages viewed', value: 'path' },
      { label: 'Page count', value: 'pageCount' },
      { label: 'Original landing page', value: 'landingpage' },
      { label: 'Current domain', value: 'domain' },
      { label: 'First-time visitor', value: 'newvisitor' },
      { label: 'Returning visitor', value: 'returningvisitor' },
      { label: 'Session count', value: 'sessionCount' },
      { label: 'First visited', value: 'visitorAge' }
    ],
    inputs: [
      {
        $formkit: 'select',
        key: 'pageviews-operator-select',
        name: 'operator',
        options: getOperatorOptions(),
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          (condition.$type === 'landingpage' || condition.$type === 'path')
      },
      {
        $formkit: 'text',
        key: 'pageviews-value-input',
        name: 'value',
        placeholder: '/pricing',
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          (condition.$type === 'landingpage' || condition.$type === 'path'),
        normalize: normalizePathInput,
        validate: (value: string) => validatePath(value)
      },
      // Current domain inputs
      {
        $formkit: 'select',
        key: 'pageviews-domain-operator-select',
        name: 'operator',
        options: getOperatorOptions(),
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' && condition.$type === 'domain'
      },
      {
        $formkit: 'text',
        key: 'pageviews-domain-value-input',
        name: 'value',
        placeholder: 'example.com',
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' && condition.$type === 'domain'
      },
      {
        $formkit: 'select',
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' && condition.$type === 'path',
        id: 'occurrence',
        key: 'pageviews-occurrence-select',
        name: 'occurrence',
        options: [
          { label: 'For the current page', value: 'last' },
          { label: 'For the previous page', value: 'penultimate' },
          { label: 'Has visited', value: 'any' },
          { label: 'Has not visited', value: 'not_any' }
        ]
      },
      {
        $formkit: 'select',
        can: (condition: {
          $source: string
          $type: string
          occurrence: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'path' &&
          condition.occurrence === 'any',  // Only show for "Has visited"
        key: 'pageviews-occurrence-operator-select',
        name: 'occurrenceOperator',
        options: [
          { label: 'Exactly', value: 'equals' },
          { label: 'At least', value: 'gte' },
          { label: 'At most', value: 'lte' },
          { label: 'More than', value: 'gt' },
          { label: 'Less than', value: 'lt' }
        ]
      },
      {
        $formkit: 'number',
        can: (condition: {
          $source: string
          $type: string
          occurrence: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'path' &&
          condition.occurrence === 'any',  // Only show for "Has visited"
        key: 'pageviews-occurrence-count-input',
        name: 'occurrenceCount',
        placeholder: '1'
      },
      {
        $formkit: 'content',
        can: (condition: {
          $source: string
          $type: string
          occurrence: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'path' &&
          condition.occurrence === 'any',  // Only show for "Has visited"
        content: 'times'
      },
      {
        $formkit: 'select',
        can: (condition: {
          $source: string
          $type: string
          occurrence: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'path' &&
          (condition.occurrence === 'any' || condition.occurrence === 'not_any'),
        key: 'pageviews-path-timeframe-select',
        name: 'timeframe',
        options: (condition: { occurrence: string }) => {
          // For "Has not visited", we only show these options
          if (condition.occurrence === 'not_any') {
            return [
              { label: 'Ever', value: 'ever' },
              { label: 'In the last', value: 'within' }
            ]
          }
          // For "Has visited", show all options
          return [
            { label: 'Ever', value: 'ever' },
            { label: 'In the last', value: 'within' }
          ]
        }
      },
      {
        $formkit: 'number',
        can: (condition: {
          $source: string
          $type: string
          occurrence: string
          timeframe: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'path' &&
          condition.occurrence?.includes('any') &&
          (condition.timeframe === 'within' ||
            condition.timeframe === 'outside'),
        key: 'pageviews-path-duration-input',
        name: 'duration',
        placeholder: '1'
      },
      {
        $formkit: 'select',
        can: (condition: {
          $source: string
          $type: string
          occurrence: string
          timeframe: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'path' &&
          condition.occurrence?.includes('any') &&
          (condition.timeframe === 'within' ||
            condition.timeframe === 'outside'),
        key: 'pageviews-path-unit-select',
        name: 'unit',
        options: [
          { label: 'Weeks', value: 'week' },
          { label: 'Days', value: 'day' },
          { label: 'Hours', value: 'hour' }
        ]
      },
      {
        $formkit: 'select',
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'returningvisitor',
        key: 'pageviews-timeframe-select',
        id: 'timeframe',
        name: 'timeframe',
        options: [
          { label: 'Has ever visited the website before', value: 'ever' },
          { label: 'Has visited in the last', value: 'within' },
          { label: "Hasn't visited in the last", value: 'outside' }
        ]
      },
      {
        $formkit: 'number',
        can: (condition: {
          $source: string
          $type: string
          timeframe: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'returningvisitor' &&
          (condition.timeframe === 'within' ||
            condition.timeframe === 'outside'),
        key: 'pageviews-duration-input',
        name: 'duration',
        placeholder: '1'
      },
      {
        $formkit: 'select',
        can: (condition: {
          $source: string
          $type: string
          timeframe: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'returningvisitor' &&
          (condition.timeframe === 'within' ||
            condition.timeframe === 'outside'),
        key: 'pageviews-unit-select',
        name: 'unit',
        options: [
          { label: 'Weeks', value: 'week' },
          { label: 'Days', value: 'day' },
          { label: 'Hours', value: 'hour' }
        ]
      },
      // Page count inputs
      {
        $formkit: 'select',
        key: 'pageCount-scope-select',
        name: 'scope',
        options: [
          { label: 'Any page', value: 'any' },
          { label: 'Pages matching', value: 'filtered' }
        ],
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' && condition.$type === 'pageCount'
      },
      {
        $formkit: 'select',
        key: 'pageCount-operator-select',
        name: 'operator',
        options: [
          { label: 'containing', value: 'contains' },
          { label: 'starting with', value: 'starts with' },
          { label: 'equal to', value: 'equals' },
          { label: 'not containing', value: 'does not contain' },
          { label: 'not equal to', value: 'does not equal' },
          { label: 'ending with', value: 'ends with' },
          { label: 'matching', value: 'glob' }
        ],
        can: (condition: { $source: string; $type: string; scope: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'pageCount' &&
          condition.scope === 'filtered'
      },
      {
        $formkit: 'text',
        key: 'pageCount-value-input',
        name: 'value',
        placeholder: '/help',
        can: (condition: { $source: string; $type: string; scope: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'pageCount' &&
          condition.scope === 'filtered',
        validate: (value: string) => validatePath(value)
      },
      {
        $formkit: 'select',
        key: 'pageCount-occurrence-operator',
        name: 'occurrenceOperator',
        options: [
          { label: 'At least', value: 'gte' },
          { label: 'At most', value: 'lte' },
          { label: 'Exactly', value: 'equals' },
          { label: 'More than', value: 'gt' },
          { label: 'Less than', value: 'lt' }
        ],
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' && condition.$type === 'pageCount'
      },
      {
        $formkit: 'number',
        key: 'pageCount-occurrence-count',
        name: 'occurrenceCount',
        placeholder: '1',
        value: 1,
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' && condition.$type === 'pageCount'
      },
      {
        $formkit: 'content',
        key: 'pageCount-times-label',
        content: 'times',
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' && condition.$type === 'pageCount'
      },
      {
        $formkit: 'select',
        key: 'pageCount-timeframe-select',
        name: 'timeframe',
        options: [
          { label: 'Ever', value: 'ever' },
          { label: 'In the last', value: 'within' }
        ],
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' && condition.$type === 'pageCount'
      },
      {
        $formkit: 'number',
        key: 'pageCount-duration-input',
        name: 'duration',
        placeholder: '7',
        can: (condition: {
          $source: string
          $type: string
          timeframe: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'pageCount' &&
          (condition.timeframe === 'within' ||
            condition.timeframe === 'outside')
      },
      {
        $formkit: 'select',
        key: 'pageCount-unit-select',
        name: 'unit',
        options: [
          { label: 'Weeks', value: 'week' },
          { label: 'Days', value: 'day' },
          { label: 'Hours', value: 'hour' }
        ],
        can: (condition: {
          $source: string
          $type: string
          timeframe: string
        }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'pageCount' &&
          (condition.timeframe === 'within' ||
            condition.timeframe === 'outside')
      },
      // Session count inputs
      {
        $formkit: 'select',
        key: 'sessionCount-operator-select',
        name: 'occurrenceOperator',
        options: [
          { label: 'At least', value: 'gte' },
          { label: 'At most', value: 'lte' },
          { label: 'Exactly', value: 'equals' },
          { label: 'More than', value: 'gt' },
          { label: 'Less than', value: 'lt' }
        ],
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'sessionCount'
      },
      {
        $formkit: 'number',
        key: 'sessionCount-count-input',
        name: 'occurrenceCount',
        placeholder: '3',
        value: 1,
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'sessionCount'
      },
      // Visitor age inputs
      {
        $formkit: 'select',
        key: 'visitorAge-operator-select',
        name: 'occurrenceOperator',
        options: [
          { label: 'At least', value: 'gte' },
          { label: 'At most', value: 'lte' },
          { label: 'More than', value: 'gt' },
          { label: 'Less than', value: 'lt' },
          { label: 'Exactly', value: 'equals' }
        ],
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'visitorAge'
      },
      {
        $formkit: 'number',
        key: 'visitorAge-duration-input',
        name: 'duration',
        placeholder: '14',
        value: 7,
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'visitorAge'
      },
      {
        $formkit: 'select',
        key: 'visitorAge-unit-select',
        name: 'unit',
        options: [
          { label: 'Weeks', value: 'week' },
          { label: 'Days', value: 'day' },
          { label: 'Hours', value: 'hour' }
        ],
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'visitorAge'
      },
      {
        $formkit: 'content',
        key: 'visitorAge-ago-label',
        content: 'ago',
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === 'pageviews' &&
          condition.$type === 'visitorAge'
      }
    ]
  }
}

export default pageviewsSource
